import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import API from '../api/api';
import logo from '../assets/logo.png';
import '../styles/superadmin.css';
import '../styles/newrecruiter.css';
import ProfileDropdown from '../components/ProfileDropdown';
import Toast from '../components/Toast';
import { clearDraft, loadDraft, saveDraft } from '../utils/draftStorage';
import { getStoredUserForRole } from '../utils/authStorage';
 
const ADD_RECRUITER_DRAFT_KEY = 'superadmin:add-recruiter';
 
// ── Sidebar icons ──
const IcoDashboard = ({ active }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={active ? '#fff' : '#3B5A8A'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
    <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
  </svg>
);
const IcoRecruiters = ({ active }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={active ? '#fff' : '#3B5A8A'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);
const IcoAnalytics = ({ active }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={active ? '#fff' : '#3B5A8A'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/>
    <line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/>
  </svg>
);
const IcoSMTP = ({ active }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={active ? '#fff' : '#3B5A8A'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
);
const IcoLogs = ({ active }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={active ? '#fff' : '#3B5A8A'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
    <line x1="10" y1="9" x2="8" y2="9"/>
  </svg>
);
 
const IcoEdit = () => (
  <svg width="15" height="15" viewBox="0 0 20 20" fill="none">
    <path d="M11.7 4.1 15.9 8.3M3.3 16.7l4.4-.9 8.7-8.7a2 2 0 0 0-2.8-2.8L4.9 13l-1.6 3.7Z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
 
const SIDEBAR_ITEMS = [
  { label: 'Dashboard', Icon: IcoDashboard },
  { label: 'Recruiters', Icon: IcoRecruiters },
  { label: 'Sections', Icon: IcoLogs, path: '/superadmin/sections' },
  { label: 'Question Bank', Icon: IcoLogs, path: '/superadmin/questionbank' },
  { label: 'Analytics', Icon: IcoAnalytics },
  { label: 'Logs', Icon: IcoLogs },
  { label: 'SMTP Settings', Icon: IcoSMTP },
];
 
const formatAuditAction = (action) =>
  (action || 'activity')
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
 
const formatAuditTimestamp = (timestamp) => {
  if (!timestamp) return '-';
  const value = new Date(timestamp);
  if (Number.isNaN(value.getTime())) return '-';
 
  return value.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};
 
const getAuditSummary = (details) => {
  if (!details || typeof details !== 'object') return '';
  if (details.title) return details.title;
  if (details.name) return details.name;
  if (details.email) return details.email;
  if (details.questionPreview) return details.questionPreview;
  if (typeof details.inserted === 'number') return `${details.inserted} imported`;
  if (typeof details.assignedCount === 'number') return `${details.assignedCount} assigned`;
  return '';
};
 
// ── Add Recruiter Modal ──
function AddRecruiterModal({ onClose, onAdded }) {
  const [form, setForm] = useState(() => loadDraft(ADD_RECRUITER_DRAFT_KEY, {
    name: '',
    email: '',
    password: '',
    company: 'Inteliment',
    role: 'junior_recruiter',
  }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
 
  useEffect(() => {
    saveDraft(ADD_RECRUITER_DRAFT_KEY, form);
  }, [form]);
 
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) { setErr('All fields required'); return; }
    setSaving(true); setErr('');
    try {
      await API.post('/admin/recruiters', form);
      clearDraft(ADD_RECRUITER_DRAFT_KEY);
      onAdded();
      onClose();
    } catch (ex) {
      setErr(ex.response?.data?.message || 'Failed to create recruiter');
    } finally { setSaving(false); }
  };
 
  const handleOverlayClose = () => {
    onClose({
      reason: 'overlay',
      draftSaved: true,
    });
  };
 
  const handleCancel = () => {
    onClose({
      reason: 'cancel',
      draftSaved: false,
    });
  };
 
  return (
    <div className="sa-modal-overlay" onClick={handleOverlayClose}>
      <div className="sa-modal" onClick={e => e.stopPropagation()}>
        <h3>Add Recruiter</h3>
        <form onSubmit={handleSubmit}>
          <div className="sa-form-row">
            <div className="sa-form-group">
              <label className="sa-form-label">Name<span className="required-star">*</span></label>
              <input className="sa-form-input" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="Full name" />
            </div>
            <div className="sa-form-group">
              <label className="sa-form-label">Email<span className="required-star">*</span></label>
              <input className="sa-form-input" type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="recruiter@company.com" />
            </div>
          </div>
          <div className="sa-form-row">
            <div className="sa-form-group">
              <label className="sa-form-label">Password<span className="required-star">*</span></label>
              <input className="sa-form-input" type="password" value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))} placeholder="Min 8 characters" />
            </div>
            <div className="sa-form-group">
              <label className="sa-form-label">Role</label>
              <select
                className="sa-form-input"
                value={form.role}
                onChange={e => setForm(f => ({...f, role: e.target.value}))}
              >
                <option value="junior_recruiter">Junior Recruiter</option>
                <option value="senior_recruiter">Senior Recruiter</option>
              </select>
            </div>
          </div>
          <div className="sa-form-row">
            <div className="sa-form-group">
              <label className="sa-form-label">Company</label>
              <input className="sa-form-input" value={form.company} onChange={e => setForm(f => ({...f, company: e.target.value}))} />
            </div>
          </div>
          {err && <p className="sa-msg sa-msg-err">{err}</p>}
          <div className="sa-modal-btns">
            <button type="button" className="sa-modal-cancel" onClick={handleCancel}>Cancel</button>
            <button type="submit" className="sa-add-btn" disabled={saving}>{saving ? 'Creating...' : 'Create'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
 
// ── Edit Recruiter Modal ──
function EditRecruiterModal({ recruiter, onClose, onSaved }) {
  const [form, setForm] = useState({ name: recruiter.name, email: recruiter.email, company: recruiter.company || '', role: recruiter.role || 'junior_recruiter' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
 
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { setErr('Name and email are required'); return; }
    setSaving(true); setErr('');
    try {
      await API.put(`/admin/recruiters/${recruiter.id}`, form);
      onSaved();
      onClose();
    } catch (ex) {
      setErr(ex.response?.data?.message || 'Failed to update recruiter');
    } finally { setSaving(false); }
  };
 
  return (
    <div className="sa-modal-overlay" onClick={onClose}>
      <div className="sa-modal" onClick={e => e.stopPropagation()}>
        <h3>Edit Recruiter</h3>
        <form onSubmit={handleSubmit}>
          <div className="sa-form-row">
            <div className="sa-form-group">
              <label className="sa-form-label">Name<span className="required-star">*</span></label>
              <input className="sa-form-input" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="Full name" />
            </div>
            <div className="sa-form-group">
              <label className="sa-form-label">Email<span className="required-star">*</span></label>
              <input className="sa-form-input" type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} />
            </div>
          </div>
          <div className="sa-form-row">
            <div className="sa-form-group">
              <label className="sa-form-label">Company</label>
              <input className="sa-form-input" value={form.company} onChange={e => setForm(f => ({...f, company: e.target.value}))} />
            </div>
            <div className="sa-form-group">
              <label className="sa-form-label">Role</label>
              <select
                className="sa-form-input"
                value={form.role}
                onChange={e => setForm(f => ({...f, role: e.target.value}))}
              >
                <option value="junior_recruiter">Junior Recruiter</option>
                <option value="senior_recruiter">Senior Recruiter</option>
              </select>
            </div>
          </div>
          {err && <p className="sa-msg sa-msg-err">{err}</p>}
          <div className="sa-modal-btns">
            <button type="button" className="sa-modal-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="sa-add-btn" disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
 
// ── Reset Password Modal ──
function ResetPasswordModal({ recruiter, onClose }) {
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
 
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { setErr('Password must be at least 8 characters'); return; }
    setSaving(true); setErr(''); setMsg('');
    try {
      await API.post(`/admin/recruiters/${recruiter.id}/reset-password`, { password });
      setMsg('Password reset successfully');
      setTimeout(onClose, 1500);
    } catch (ex) {
      setErr(ex.response?.data?.message || 'Failed to reset password');
    } finally { setSaving(false); }
  };
 
  return (
    <div className="sa-modal-overlay" onClick={onClose}>
      <div className="sa-modal" onClick={e => e.stopPropagation()}>
        <h3>Reset Password</h3>
        <p style={{ color: '#64748B', fontSize: '0.875rem', marginBottom: '1rem' }}>
          Reset password for <strong>{recruiter.name}</strong> ({recruiter.email})
        </p>
        <form onSubmit={handleSubmit}>
          <div className="sa-form-group">
            <label className="sa-form-label">New Password<span className="required-star">*</span></label>
            <input className="sa-form-input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 8 characters" />
          </div>
          {err && <p className="sa-msg sa-msg-err">{err}</p>}
          {msg && <p className="sa-msg sa-msg-ok">{msg}</p>}
          <div className="sa-modal-btns">
            <button type="button" className="sa-modal-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="sa-add-btn" disabled={saving}>{saving ? 'Resetting...' : 'Reset Password'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
 
// ── Main Component ──
export default function SuperAdminDashboard() {
  const storedUser = getStoredUserForRole('superadmin');
  const location = useLocation();
  const tabParam = new URLSearchParams(location.search).get('tab');
  const VALID_TABS = ['Dashboard', 'Recruiters', 'Analytics', 'Logs', 'SMTP Settings'];
  // eslint-disable-next-line no-unused-vars
  const [activeItem, setActiveItem] = useState(VALID_TABS.includes(tabParam) ? tabParam : 'Dashboard');
  const [recruiters, setRecruiters] = useState([]);
  const [recLoading, setRecLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsSearch, setLogsSearch] = useState('');
  const [logsTotal, setLogsTotal] = useState(0);
  const [smtp, setSmtp] = useState({ smtp_host: '', smtp_port: '587', smtp_user: '', smtp_pass: '', smtp_from: '', smtp_secure: 'false' });
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpMsg, setSmtpMsg] = useState('');
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [smtpTestMsg, setSmtpTestMsg] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editRecruiter, setEditRecruiter] = useState(null);
  const [resetRecruiter, setResetRecruiter] = useState(null);
  const [toast, setToast] = useState(null);
 
  const fetchRecruiters = () => {
    setRecLoading(true);
    API.get('/admin/recruiters')
      .then(res => setRecruiters(Array.isArray(res.data) ? res.data : []))
      .catch(() => setRecruiters([]))
      .finally(() => setRecLoading(false));
  };
 
  const fetchLogs = (searchText = logsSearch) => {
    setLogsLoading(true);
    API.get('/admin/logs', {
      params: {
        limit: 100,
        search: searchText?.trim() || undefined,
      },
    })
      .then((res) => {
        setLogs(Array.isArray(res.data?.logs) ? res.data.logs : []);
        setLogsTotal(Number(res.data?.total || 0));
      })
      .catch(() => {
        setLogs([]);
        setLogsTotal(0);
      })
      .finally(() => setLogsLoading(false));
  };
 
  useEffect(() => {
    if (activeItem === 'Recruiters') fetchRecruiters();
    if (activeItem === 'Analytics' && !analytics) {
      setAnalyticsLoading(true);
      API.get('/admin/analytics')
        .then(res => setAnalytics(res.data))
        .catch(() => setAnalytics(null))
        .finally(() => setAnalyticsLoading(false));
    }
    if (activeItem === 'SMTP Settings') {
      API.get('/admin/smtp').then(res => setSmtp(s => ({...s, ...res.data, smtp_pass: ''}))).catch(() => {});
    }
  }, [activeItem, analytics]);
 
  useEffect(() => {
    if (activeItem !== 'Logs') return undefined;
 
    const timeoutId = setTimeout(() => {
      fetchLogs(logsSearch);
    }, 250);
 
    return () => clearTimeout(timeoutId);
  }, [logsSearch, activeItem]);
 
  const handleToggleStatus = async (rec) => {
    try {
      await API.patch(`/admin/recruiters/${rec.id}/status`, { is_active: !rec.is_active });
      fetchRecruiters();
    } catch (ex) {
      alert(ex.response?.data?.message || 'Failed to update status');
    }
  };
 
  const normalizeSmtpForm = (current) => {
    const next = {
      ...current,
      smtp_host: String(current.smtp_host || '').trim(),
      smtp_port: String(current.smtp_port || '587').trim(),
      smtp_user: String(current.smtp_user || '').trim(),
      smtp_from: String(current.smtp_from || '').trim(),
      smtp_secure: String(current.smtp_secure || 'false'),
    };
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
 
    if (emailPattern.test(next.smtp_host)) {
      const hostEmail = next.smtp_host.toLowerCase();
      const domain = hostEmail.split('@')[1];
      next.smtp_user = hostEmail;
      if (!next.smtp_from || next.smtp_from.toLowerCase().includes('noreply@company.com')) {
        next.smtp_from = hostEmail;
      }
      if (domain === 'gmail.com' || domain === 'googlemail.com') {
        next.smtp_host = 'smtp.gmail.com';
      } else if (['outlook.com', 'hotmail.com', 'live.com'].includes(domain)) {
        next.smtp_host = 'smtp.office365.com';
      } else if (domain) {
        next.smtp_host = `smtp.${domain}`;
      }
    }
    if (!next.smtp_host && next.smtp_user) {
  const domain = next.smtp_user.split('@')[1]?.toLowerCase();
  if (domain === 'gmail.com' || domain === 'googlemail.com') {
    next.smtp_host = 'smtp.gmail.com';
  } else if (['outlook.com', 'hotmail.com', 'live.com'].includes(domain)) {
    next.smtp_host = 'smtp.office365.com';
  } else if (domain) {
    next.smtp_host = `smtp.${domain}`;
  }
}
    if (next.smtp_host.toLowerCase() === 'gmail.com') next.smtp_host = 'smtp.gmail.com';
    if (next.smtp_user) next.smtp_user = next.smtp_user.toLowerCase();
    if (!next.smtp_from && next.smtp_user) next.smtp_from = next.smtp_user;
    if (next.smtp_port === '465') next.smtp_secure = 'true';
    return next;
  };
 
  const handleSaveSMTP = async (e) => {
    e.preventDefault();
    setSmtpSaving(true); setSmtpMsg('');
    try {
      const normalized = normalizeSmtpForm(smtp);
      setSmtp(normalized);
      const res = await API.put('/admin/smtp', normalized);
      if (res.data?.settings) setSmtp(s => ({ ...s, ...res.data.settings, smtp_pass: s.smtp_pass }));
      setSmtpMsg('Settings saved successfully.');
    } catch (ex) { setSmtpMsg(ex.response?.data?.message || 'Failed to save settings.'); }
    finally { setSmtpSaving(false); }
  };
 
  const handleTestSMTP = async () => {
    setSmtpTesting(true); setSmtpTestMsg(null);
    try {
      const normalized = normalizeSmtpForm(smtp);
      setSmtp(normalized);
      const res = await API.post('/admin/smtp/test', normalized);
      if (res.data?.settings) setSmtp(s => ({ ...s, ...res.data.settings, smtp_pass: s.smtp_pass }));
      setSmtpTestMsg({ ok: true, text: res.data.message });
    } catch (ex) {
      setSmtpTestMsg({ ok: false, text: ex.response?.data?.message || 'Connection failed.' });
    } finally { setSmtpTesting(false); }
  };
 
  const kpiColor = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6'];
 
  const handleCloseAddRecruiterModal = (closeMeta = {}) => {
    setShowAddModal(false);
 
    if (closeMeta.reason === 'overlay' && closeMeta.draftSaved) {
      setToast({
        message: 'Draft saved. You can continue creating this recruiter later.',
        type: 'success',
      });
    }
  };
 
  return (
    <div className={`sa-page ${activeItem === 'Logs' ? 'sa-page--logs' : ''}`}>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <div className="nr-navbar-wrapper">
        <img src={logo} alt="Inteliment Logo" className="nr-navbar-logo-img" />
        <div className="nr-navbar-greeting">
          <h2 className="nr-navbar-greeting-title">SuperAdmin Portal</h2>
          <p className="nr-navbar-greeting-sub">{storedUser.name || storedUser.email}</p>
        </div>
        <div className="nr-navbar-actions">
          <div className="nr-navbar-profile">
            <ProfileDropdown user={storedUser.name || storedUser.email} email={storedUser.email} />
          </div>
        </div>
      </div>
 
        <aside className="sa-sidebar">
          {SIDEBAR_ITEMS.map(({ label, Icon, path }, index) => (
            <div
              key={label}
              className={`sa-sidebar-item ${activeItem === label ? 'active' : ''}`}
              onClick={() => path ? window.location.assign(path) : setActiveItem(label)}
              style={{ marginTop: index === 0 ? '60px' : '0' }}
            >
              <span className="sa-sidebar-icon"><Icon active={activeItem === label} /></span>
              <span>{label}</span>
            </div>
          ))}
        </aside>
 
        <main className={`sa-main ${activeItem === 'Logs' ? 'sa-main--logs' : ''}`}>
          {/* ── Dashboard Overview ── */}
          {activeItem === 'Dashboard' && (
            <>
              <h2 className="sa-section-title">Welcome, {(storedUser.name || 'SuperAdmin').split(' ')[0]} !</h2>
              <div className="sa-kpi-row">
                {[
                  { label: 'Manage Recruiters', sub: 'Create & deactivate recruiter accounts', color: '#3B82F6', action: 'Recruiters' },
                  { label: 'Analytics', sub: 'View platform-wide stats and KPIs', color: '#10B981', action: 'Analytics' },
                  { label: 'Activity Logs', sub: 'See who created, edited, assigned or signed in', color: '#8B5CF6', action: 'Logs' },
                  { label: 'SMTP Settings', sub: 'Configure email notification server', color: '#F59E0B', action: 'SMTP Settings' },
                ].map((c, i) => (
                  <div key={i} className="sa-kpi-card" style={{ cursor: 'pointer' }} onClick={() => setActiveItem(c.action)}>
                    <div className="sa-kpi-bar" style={{ background: c.color }} />
                    <span className="sa-kpi-label">{c.label}</span>
                    <p style={{ fontSize: '12px', color: '#64748B', margin: '6px 0 0' }}>{c.sub}</p>
                  </div>
                ))}
              </div>
            </>
          )}
 
          {/* ── Recruiters ── */}
          {activeItem === 'Recruiters' && (
            <>
              <h2 className="sa-section-title">Recruiter Management</h2>
              <div className="sa-table-card">
                <div className="sa-table-header">
                  <h3>All Recruiters</h3>
                  <button className="sa-add-btn" onClick={() => setShowAddModal(true)}>+ Add Recruiter</button>
                </div>
                {recLoading ? (
                  <p style={{ padding: '20px', color: '#64748B' }}>Loading...</p>
                ) : (
                  <table className="sa-table">
                    <thead>
                      <tr>
                        <th>Name</th><th>Email</th><th>Role</th><th>Company</th><th>Status</th><th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recruiters.length === 0 ? (
                        <tr><td colSpan={6} style={{ textAlign: 'center', color: '#64748B' }}>No recruiters found.</td></tr>
                      ) : recruiters.map(r => (
                        <tr key={r.id}>
                          <td>{r.name}</td>
                          <td style={{ color: '#3B82F6' }}>{r.email}</td>
                          <td style={{ textTransform: 'capitalize' }}>{r.role}</td>
                          <td>{r.company}</td>
                          <td>
                            <span className={`sa-status-pill ${r.is_active ? 'sa-status-active' : 'sa-status-inactive'}`}>
                              {r.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                            <button
                              className={`sa-action-btn ${r.is_active ? 'sa-action-deactivate' : 'sa-action-activate'}`}
                              onClick={() => handleToggleStatus(r)}
                            >
                              {r.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                            <button className="sa-action-btn sa-action-edit" title="Edit" aria-label="Edit" onClick={() => setEditRecruiter(r)}><IcoEdit /></button>
                            <button className="sa-action-btn sa-action-reset" onClick={() => setResetRecruiter(r)}>Reset Password</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
 
          {/* ── Analytics ── */}
          {activeItem === 'Analytics' && (
            <>
              <h2 className="sa-section-title">Platform Analytics</h2>
              {analyticsLoading ? (
                <p style={{ color: '#64748B' }}>Loading analytics...</p>
              ) : !analytics ? (
                <p style={{ color: '#EF4444' }}>Could not load analytics data.</p>
              ) : (
                <>
                  <div className="sa-kpi-row">
                    {[
                      { label: 'Total Users', value: analytics.users.total, sub: `${analytics.users.candidates} candidates · ${analytics.users.recruiters} recruiters`, color: kpiColor[0] },
                      { label: 'Total Exams', value: analytics.exams.total, sub: `${analytics.exams.published} published · ${analytics.exams.draft} draft`, color: kpiColor[1] },
                      { label: 'Assignments', value: analytics.assignments.total, sub: `${analytics.assignments.completed} completed`, color: kpiColor[2] },
                      { label: 'Avg Score', value: `${analytics.performance.avg_score}%`, sub: `${analytics.performance.total_attempts} total attempts`, color: kpiColor[3] },
                    ].map((kpi, i) => (
                      <div key={i} className="sa-kpi-card">
                        <div className="sa-kpi-bar" style={{ background: kpi.color }} />
                        <span className="sa-kpi-label">{kpi.label}</span>
                        <span className="sa-kpi-number">{kpi.value}</span>
                        <span className="sa-kpi-sub">{kpi.sub}</span>
                      </div>
                    ))}
                  </div>
 
                  <div className="sa-table-card">
                    <div className="sa-table-header"><h3>Question Bank — Sections</h3></div>
                    <table className="sa-table">
                      <thead><tr><th>Section</th><th>Questions</th></tr></thead>
                      <tbody>{(analytics.sections || []).map((s, i) => (
                        <tr key={i}><td>{s.name}</td><td>{s.question_count}</td></tr>
                      ))}</tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
 
          {/* ── Logs ── */}
          {activeItem === 'Logs' && (
            <>
              <div className="sa-logs-header">
                <div>
                  <h2 className="sa-section-title" style={{ marginBottom: '8px' }}>Activity Logs</h2>
                  <p className="sa-logs-subtitle">Track who is doing what across recruiters, exams, questions, sections, and candidates.</p>
                </div>
                <button className="sa-add-btn" onClick={() => fetchLogs()}>Refresh</button>
              </div>
 
              <div className="sa-table-card sa-logs-card">
                <div className="sa-table-header sa-logs-toolbar">
                  <h3>Recent Activity</h3>
                  <div className="sa-logs-search-wrap">
                    <input
                      className="sa-form-input sa-logs-search"
                      value={logsSearch}
                      onChange={(e) => setLogsSearch(e.target.value)}
                      placeholder="Search by user, action, entity, exam, section..."
                    />
                    <span className="sa-logs-total">{logsTotal} entries</span>
                  </div>
                </div>
 
                {logsLoading ? (
                  <p style={{ padding: '20px', color: '#64748B' }}>Loading logs...</p>
                ) : logs.length === 0 ? (
                  <p style={{ padding: '20px', color: '#64748B' }}>No activity found yet.</p>
                ) : (
                  <div className="sa-logs-table-wrap">
                    <table className="sa-table">
                      <thead>
                        <tr>
                          <th>When</th>
                          <th>User</th>
                          <th>Action</th>
                          <th>Entity</th>
                          <th>Details</th>
                          <th>IP Address</th>
                        </tr>
                      </thead>
                      <tbody>
                        {logs.map((log) => (
                          <tr key={log.id}>
                            <td>{formatAuditTimestamp(log.timestamp)}</td>
                            <td>
                              <div className="sa-log-actor">
                                <strong>{log.actor_name || 'System'}</strong>
                                <span className="sa-email-text">{log.actor_email || '-'}</span>
                              </div>
                            </td>
                            <td>{formatAuditAction(log.action)}</td>
                            <td>
                              <span className="sa-entity-badge">
                                {log.entity_type}
                                {log.entity_id ? ` #${log.entity_id}` : ''}
                              </span>
                            </td>
                            <td>
                              <div className="sa-log-details">
                                <span>{getAuditSummary(log.details) || '-'}</span>
                                {log.actor_role && <small>{log.actor_role}</small>}
                              </div>
                            </td>
                            <td>{log.ip_address || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
 
          {/* ── SMTP Settings ── */}
          {activeItem === 'SMTP Settings' && (
            <div style={{ maxWidth: '540px' }}>
              <h2 className="sa-section-title">SMTP Configuration</h2>
              <p style={{ fontSize: '13px', color: '#64748B', marginBottom: '20px' }}>
                Configure the mail server used to send exam assignment notifications to candidates.
              </p>
 
              <div style={{
                background: '#fff',
                borderRadius: '16px',
                boxShadow: '0 2px 16px rgba(77,139,241,0.08)',
                overflow: 'hidden',
              }}>
                {/* Header strip */}
                <div style={{
                  background: 'linear-gradient(135deg, #2D6BE4 0%, #4F8EF7 100%)',
                  padding: '16px 22px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}>
                  <div style={{
                    width: '36px', height: '36px', borderRadius: '10px',
                    background: 'rgba(255,255,255,0.2)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                  </div>
                  <div>
                    <div style={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Mail Server Settings</div>
                    <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '11px' }}>SMTP credentials for outbound email</div>
                  </div>
                </div>
 
                {/* Form body */}
                <div style={{ padding: '20px' }}>
                  {/* ✅ FIX: autoComplete="off" prevents browser autofill from faking values */}
                  <form onSubmit={handleSaveSMTP} autoComplete="off">
 
                    {/* Row 1 */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748B', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>SMTP Host</label>
                        <input
                          className="sa-form-input sa-raw-input"
                          style={{ width: '100%', boxSizing: 'border-box' }}
                          placeholder="smtp.gmail.com"
                          autoComplete="off"
                          value={smtp.smtp_host}
                          onChange={e => setSmtp(s => ({...s, smtp_host: e.target.value.toLowerCase()}))}
                          onFocus={e => setSmtp(s => ({...s, smtp_host: e.target.value.toLowerCase()}))}
                          onBlur={e => setSmtp(s => ({...s, smtp_host: e.target.value.toLowerCase()}))}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748B', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Port</label>
                        <input
                          className="sa-form-input"
                          style={{ width: '100%', boxSizing: 'border-box' }}
                          placeholder="587"
                          autoComplete="off"
                          value={smtp.smtp_port}
                          onChange={e => setSmtp(s => ({...s, smtp_port: e.target.value}))}
                        />
                      </div>
                    </div>
 
                    {/* Row 2 */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748B', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Username / Email</label>
                        <input
                          className="sa-form-input sa-email-input sa-raw-input"
                          style={{ width: '100%', boxSizing: 'border-box' }}
                          placeholder="noreply@company.com"
                          autoComplete="off"
                          value={smtp.smtp_user}
                          onChange={e => setSmtp(s => ({...s, smtp_user: e.target.value.toLowerCase()}))}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748B', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Password / App Key</label>
                        <input
                          className="sa-form-input sa-email-input sa-raw-input"
                          style={{ width: '100%', boxSizing: 'border-box' }}
                          type="password"
                          placeholder="App password"
                          autoComplete="new-password"
                          value={smtp.smtp_pass}
                          onChange={e => setSmtp(s => ({...s, smtp_pass: e.target.value}))}
                        />
                      </div>
                    </div>
 
                    {/* Row 3 */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748B', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>From Address</label>
                        <input
                          className="sa-form-input sa-email-input sa-raw-input"
                          style={{ width: '100%', boxSizing: 'border-box' }}
                          placeholder="Aptitude Portal <noreply@company.com>"
                          autoComplete="off"
                          value={smtp.smtp_from}
                          onChange={e => {
                            const value = e.target.value;
                            setSmtp(s => ({...s, smtp_from: value.includes('<') ? value : value.toLowerCase()}));
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748B', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Secure (TLS)</label>
                        <select
                          className="sa-form-select"
                          style={{ width: '100%', boxSizing: 'border-box' }}
                          value={smtp.smtp_secure}
                          onChange={e => setSmtp(s => ({...s, smtp_secure: e.target.value}))}
                        >
                          <option value="false">STARTTLS (Port 587)</option>
                          <option value="true">SSL/TLS (Port 465)</option>
                        </select>
                      </div>
                    </div>
 
                    {/* Divider */}
                    <div style={{ borderTop: '1px solid #F1F5F9', marginBottom: '16px' }} />
 
                    {/* Actions */}
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <button
                        type="submit"
                        disabled={smtpSaving}
                        style={{
                          background: 'linear-gradient(135deg, #2D6BE4 0%, #4F8EF7 100%)',
                          color: '#fff', border: 'none', borderRadius: '10px',
                          padding: '9px 20px', fontSize: '13px', fontWeight: 600,
                          cursor: smtpSaving ? 'not-allowed' : 'pointer',
                          opacity: smtpSaving ? 0.7 : 1,
                          display: 'flex', alignItems: 'center', gap: '8px',
                          boxShadow: '0 4px 12px rgba(45,107,228,0.3)',
                          fontFamily: "'Inter', sans-serif",
                        }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2l11 0 5 5v11a2 2 0 0 1-2 2z"/>
                          <polyline points="17 21 17 13 7 13 7 21"/>
                          <polyline points="7 3 7 8 15 8"/>
                        </svg>
                        {smtpSaving ? 'Saving...' : 'Save Settings'}
                      </button>
 
                      <button
                        type="button"
                        className="sa-test-btn"
                        disabled={smtpTesting}
                        onClick={handleTestSMTP}
                      >
                        {smtpTesting ? (
                          <><span className="sa-test-spinner" />Testing...</>
                        ) : (
                          <>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.7 12a19.79 19.79 0 0 1-3-8.59A2 2 0 0 1 3.72 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                            </svg>
                            Test Connection
                          </>
                        )}
                      </button>
                    </div>
 
                    {/* Messages */}
                    {smtpMsg && (
                      <div style={{
                        marginTop: '14px', padding: '9px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 500,
                        background: smtpMsg.includes('success') ? '#F0FDF4' : '#FFF1F2',
                        color: smtpMsg.includes('success') ? '#15803D' : '#BE123C',
                        border: `1px solid ${smtpMsg.includes('success') ? '#BBF7D0' : '#FECDD3'}`,
                      }}>
                        {smtpMsg}
                      </div>
                    )}
 
                    {smtpTestMsg && (
                      <div className={`sa-test-result ${smtpTestMsg.ok ? 'sa-test-result--ok' : 'sa-test-result--err'}`} style={{ marginTop: '10px' }}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          {smtpTestMsg.ok
                            ? <><circle cx="12" cy="12" r="10"/><path d="M9 12l2 2 4-4"/></>
                            : <><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></>
                          }
                        </svg>
                        {smtpTestMsg.text}
                      </div>
                    )}
 
                  </form>
                </div>
              </div>
            </div>
          )}
        </main>
 
      {showAddModal && (
        <AddRecruiterModal onClose={handleCloseAddRecruiterModal} onAdded={fetchRecruiters} />
      )}
      {editRecruiter && (
        <EditRecruiterModal recruiter={editRecruiter} onClose={() => setEditRecruiter(null)} onSaved={fetchRecruiters} />
      )}
      {resetRecruiter && (
        <ResetPasswordModal recruiter={resetRecruiter} onClose={() => setResetRecruiter(null)} />
      )}
    </div>
  );
}
 
 
